import { useEffect, useMemo, useState } from "react";

import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useSearchParams } from "react-router-dom";
import { Alert } from "react-bootstrap";

import { CURRENCIES, Roles } from "../../utils/constants";
import { getCategories } from "../../services/CategoryService";
import StatisticsFilter from "./StatisticsFilter";
import { getStatistics } from "../../services/EmploymentService";
import { formatDate, getCurrentUser } from "../../utils/utils";
import SecondaryButton from "../../components/SecondaryButton";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const Statistics = () => {
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState(null);
  const [filterData, setFilterData] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filteredData, setFilteredData] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [totalAmount, setTotalAmount] = useState([]);
  const user = getCurrentUser();

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 15,
          font: {
            family: "Sarabun, serif",
            size: "14px",
            weight: "500",
          },
        },
      },
      title: {
        display: true,
        font: {
          size: "17px",
          weight: "bold",
          color: "black",
        },
        text: "Availability per category per week",
      },
    },
  };

  const startDate = useMemo(() => {
    const sDate = filterData?.from_date ? filterData.from_date : new Date();
    if (searchParams.get("time") === "history") {
      sDate.setMonth(sDate.getMonth() - 2);
    }
    return sDate;
  }, [searchParams, filterData?.from_date]);
  const endDate = useMemo(() => {
    const eDate = filterData?.to_date ? filterData.to_date : new Date();
    if (!filterData?.from_date && !filterData?.to_date) {
      if (
        !searchParams.get("time") ||
        searchParams.get("time") === "projections"
      ) {
        eDate.setMonth(startDate.getMonth() + 2);
      }
    }
    return eDate;
  }, [searchParams, startDate, filterData?.to_date]);

  const weeksBetweenAsDates = (defaultStartDate, defaultEndDate) => {
    let start = new Date(defaultStartDate);
    let end = new Date(defaultEndDate);
    let sDate;
    let eDate;
    let dateArr = [];

    while (start <= end) {
      if (start.getDay() === 1 || (dateArr.length === 0 && !sDate)) {
        sDate = new Date(start.getTime());
      }
      if (
        (sDate && start.getDay() === 0) ||
        start.getTime() === end.getTime()
      ) {
        eDate = new Date(start.getTime());
      }
      if (sDate && eDate) {
        dateArr.push({ startDate: sDate, endDate: eDate });
        sDate = null;
        eDate = null;
      }
      start.setDate(start.getDate() + 1);
    }

    return dateArr;
  };

  const checkDates = (s1, e1, s2, e2) => {
    return (
      new Date(`${s1.getFullYear()}-${s1.getMonth() + 1}-${s1.getUTCDate()}`) <=
        e2 &&
      new Date(`${e1.getFullYear()}-${e1.getMonth() + 1}-${e1.getUTCDate()}`) >=
        s2
    );
  };

  const convertData = (requests) => {
    // Get the weeks between the filtering dates, and accordingly separate the AVG number of hours
    const graphData = [];
    const filterWeeks = weeksBetweenAsDates(startDate, endDate);
    filterWeeks.map((week, index) => {
      graphData.push({ ...week, categories: null });
      requests.forEach((request) => {
        const requestStartDate = new Date(request.from_date);
        const requestEndDate = new Date(request.to_date);

        if (
          checkDates(
            requestStartDate,
            requestEndDate,
            week.startDate,
            week.endDate
          )
        ) {
          if (request?.categories) {
            const weeksCount = weeksBetweenAsDates(
              requestStartDate,
              requestEndDate
            ).length;

            request.categories.forEach((category) => {
              if (!graphData[index].categories) {
                graphData[index].categories = {};
              }

              if (
                !Object.keys(graphData[index].categories).includes(
                  category.id.toString()
                )
              ) {
                graphData[index].categories[category.id] = 0;
              }

              let hours = 0;
              if (category.hours) {
                hours = category.hours / (weeksCount === 0 ? 1 : weeksCount);
              }

              graphData[index].categories[category.id] += hours;
              graphData[index].categories[category.id] =
                Math.round(graphData[index].categories[category.id] * 2) / 2;
            });
          }
        }
      });
    });
    return graphData;
  };

  const getCategoriesData = (graphData) => {
    const categoriesData = [];

    graphData.forEach((week, weekIndex) => {
      if (week.categories) {
        Object.keys(week.categories).forEach((categoryId, index) => {
          const catIndex = categoriesData.findIndex(
            (i) => i.categoryId === categoryId
          );

          if (catIndex !== -1) {
            categoriesData[catIndex] = {
              ...categoriesData[catIndex],
              data: [
                ...categoriesData[catIndex].data,
                week.categories[categoryId],
              ],
            };
          } else {
            const category = categories.find((i) => i.id == categoryId);
            categoriesData.push({
              label: category.name,
              categoryId,
              minBarLength: 4,
              data: [
                ...(weekIndex !== 0 ? new Array(weekIndex).fill(0) : []),
                week.categories[categoryId],
              ],
              backgroundColor:
                category?.color ||
                `#${Math.floor(Math.random() * 16777215).toString(16)}`,
            });
          }
        });
      }
    });

    // If there are less category hours then weeks, fill the rest with 0
    return categoriesData.map((categoriesData) => {
      if (categoriesData.data.length < graphData.length) {
        const fillCount = graphData.length - categoriesData.data.length;
        return {
          ...categoriesData,
          data: [...categoriesData.data, ...new Array(fillCount).fill(0)],
        };
      }
      return categoriesData;
    });
  };

  /**
   * Retrieve the categories and the statistics data from the API
   */
  useEffect(() => {
    getCategories().then((data) => setCategories(data));
    getStatistics().then((statistics) => setStatistics(statistics));
  }, []);

  /**
   * Filter the requests by start date, end date, employments and categories
   */
  useEffect(() => {
    if (statistics) {
      let amount = [];
      const requests = statistics.reduce((acc, userStats) => {
        if (
          !filterData?.employees ||
          filterData?.employees.length === 0 ||
          filterData?.employees.includes(userStats.user.email) ||
          filterData?.employees.includes(userStats.user.phone_number) ||
          filterData?.employees.includes(userStats.identifier)
        ) {
          acc.push(
            ...userStats.requests
              .filter((request) => {
                const requestStartDate = new Date(request.from_date);
                const requestEndDate = new Date(request.to_date);
                return checkDates(
                  requestStartDate,
                  requestEndDate,
                  startDate,
                  endDate
                );
              })
              .map((item) => {
                // Get the spending/earning for the user
                const currency = amount.find(
                  (i) => i.currency === userStats.currency
                );
                if (currency) {
                  amount = amount.map((i) => {
                    if (i.currency === userStats.currency) {
                      return {
                        ...i,
                        amount:
                          i.amount +
                          item.hours *
                            (userStats.hourly_rate ? userStats.hourly_rate : 0),
                      };
                    }
                    return i;
                  });
                } else {
                  amount.push({
                    currency: userStats.currency,
                    amount:
                      item.hours *
                      (userStats.hourly_rate ? userStats.hourly_rate : 0),
                  });
                }
                return {
                  ...item,
                  categories: item.categories.filter(
                    (category) =>
                      !filterData?.categories ||
                      filterData.categories.length === 0 ||
                      filterData.categories.includes(category.id)
                  ),
                };
              })
          );
          setTotalAmount(amount);
        }
        return acc;
      }, []);

      setTotalAmount(amount);
      setFilteredData(requests);
    }
  }, [searchParams, statistics, filterData, startDate, endDate]);

  /**
   * Calculate the data for the graph by adjusting the requests array and then transform it using the categories.
   */
  useEffect(() => {
    if (filteredData) {
      const graphData = convertData(filteredData);
      const datasets = getCategoriesData(graphData);
      setData({
        minBarLength: 2,
        labels: graphData.map(
          (week) =>
            `${week.startDate.getUTCMonth()}.${week.startDate.getUTCDate()}` +
            " - " +
            `${week.endDate.getUTCMonth()}.${week.endDate.getUTCDate()}`
        ),
        datasets,
      });
    }
  }, [searchParams, filteredData, categories]);

  const totalHours = useMemo(() => {
    if (data) {
      return filteredData.reduce((acc, item) => {
        const hours = item.categories.reduce((a, b) => {
          if (b?.hours) {
            return a + b.hours;
          }
          return a;
        }, 0);
        return acc + hours;
      }, 0);
    }
    return 0;
  }, [filteredData]);

  return (
    <div>
      {user.role === Roles.EMPLOYEE.value && (
        <Alert variant="success">
          <span style={{ fontWeight: "bold" }}>{totalHours} hours</span>{" "}
          committed between {formatDate(startDate)} and {formatDate(endDate)}.
          <div className="mt-3">
            <h6>Estimated Earnings:</h6>
            <ol>
              {totalAmount.map((i) => (
                <li key={i.currency}>
                  {i.amount.toLocaleString()} {i?.currency ? CURRENCIES[i.currency].symbol : ''}
                </li>
              ))}
            </ol>
          </div>
        </Alert>
      )}
      <div style={{ textAlign: "right" }}>
        <SecondaryButton onClick={() => setShowFilters((oldVal) => !oldVal)}>
          {showFilters ? "Hide Filters" : "Show Filters"}
        </SecondaryButton>
      </div>
      {showFilters && (
        <StatisticsFilter
          categories={categories}
          filterData={filterData}
          setFilterData={setFilterData}
        />
      )}
      <div className="graph">
        {data && <Bar options={options} data={data} />}
      </div>
    </div>
  );
};

export default Statistics;
